import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import pako from 'pako';
import Archive from './Archive';
import Classic from './Classic';
import Header from './Header';
import Menu from './Menu';
import Settings from './Settings';
import Statistics from './Statistics';
import useWindow from '../hooks/window';
import { ReactComponent as Loading } from '../images/loading.svg';
import { ReactComponent as Star } from '../images/star.svg';

type Board = Array<Array<number>>;
type Config = {
    id: string,
    b: 4 | 5,
    m: 'drag' | 'select'
};
type DragData = {
    row: number,
    cell: number,
    position: {
        x: number,
        y: number
    }
};
type GameData = {
    id: number,
    size: number,
    turns: number,
    puzzle: number[],
    images: string[]
};
type GameState = 'loading' | 'active' | 'finished' | 'over';
type Moves = {
    [key: number]: number
};
type Page = 'archive' | 'classic' | 'daily' | 'settings' | 'statistics';
type Props = {
    config: Config,
    isDark: boolean,
    setScheme: (scheme: 'dark' | 'light') => void
};
type Puzzle = {
    id: number,
    day: string,
    puzzle: number[],
    images: string[],
    score: number
};
type PuzzleResult = {p: number, s: number};

function* chunk<T>(array: T[], size: number): Generator<T[], void> {
    for (let x = 0, l = array.length; x < l; x += size) {
        yield array.slice(x, size + x);
    }
}

function createOrderedBoard(size: number): Board {
    return [...chunk([...Array(size * size).keys()].map((value: number): number => value + 1), size)];
}

const Game = ({ config, isDark, setScheme }: Props): JSX.Element => {
    const scores: string[] = ['Phew', 'Great', 'Great', 'Impressive', 'Impressive', 'Magnificent'];
    const stars: number[] = [0, 1, 1, 2, 2, 3];
    const moves: Moves = {4: 15, 5: 25};
    const dailyMoves = moves[config.b];
    const initialLoad = useRef<boolean>(false);
    const [dailyId, setDailyId] = useState<number>(0);
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const [showStatistics, setShowStatistics] = useState<boolean>(false);
    const [turns, setTurns] = useState<number>(0);
    const [gameState, setGameState] = useState<GameState>('loading');
    const [board, setBoard] = useState<Board>(createOrderedBoard(config.b));
    const [images, setImages] = useState<string[] | null>(null);
    const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
    const [puzzleId, setPuzzleId] = useState<number | null>(null);
    const [page, setPage] = useState<Page>('daily');
    const [copied, setCopied] = useState<boolean>(false);
    const drag = useRef<HTMLDivElement | null>(null);
    const dragData = useRef<DragData | null>(null);
    const drags = useRef<number>(0);
    const dailyRefresh = useRef<number | null>(null);
    const firstSelection = useRef<HTMLDivElement | null>(null);
    const secondSelection = useRef<HTMLDivElement | null>(null);
    const dimensions = useWindow();
    const cellSize = useMemo<number>(() => {
        const maxWidth = Math.floor((Math.min(100 * config.b, dimensions.width - 10 - (config.b - 1) * 5)) / config.b);
        const maxHeight = Math.floor((Math.min(100 * config.b, dimensions.height - 309 - (config.b - 1) * 5)) / config.b);

        return Math.min(maxWidth, maxHeight);
    }, [config, dimensions]);

    const decodePuzzle = useCallback((puzzle: string): Puzzle => {
        const convert = (encoded: string): Uint8Array => {
            const binary = atob(encoded);
            const bytes = new Uint8Array(binary.length);

            for (let i = 0, l = bytes.length; i < l; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            return new Uint8Array(bytes.buffer);
        }

        const inflated = pako.inflate(convert(puzzle));
        const dataView = new DataView(inflated.buffer.slice(inflated.byteOffset, inflated.byteLength));
        const dataLength = dataView.getUint32(0);
        let result = '';
        let position = 4;

        for (let x = 0; x < dataLength; x++) {
            result += String.fromCharCode(dataView.getUint8(position++));
        }

        return JSON.parse(result);
    }, []);

    const loadPuzzle = useCallback((puzzle: Puzzle, size = config.b) => {
        setImages(puzzle.images);
        setPuzzleId(puzzle.id);
        setGameState('active');
        setTurns(0);
        setShowMenu(false);
        setPage('daily');

        if (puzzle.id === dailyId) {
            const game = JSON.parse(atob(localStorage.getItem('daily') || 'e30='));

            if (game.id && game.id === puzzle.id && game.size === size) {
                setTurns(game.turns);

                puzzle.puzzle = game.puzzle;
            }
        }

        const board: Board = [...chunk<number>(puzzle.puzzle, size)];

        setBoard(board);
        setPuzzle(puzzle);
    }, [config, dailyId]);

    const loadDailyPuzzle = useCallback(async(): Promise<void> => {
        const now = new Date();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/daily/${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}/${config.b * config.b}`);
        const text = await response.text();
        const daily = JSON.parse(atob(localStorage.getItem('daily') || 'e30='));
        const data = decodePuzzle(text);
        const isToday = daily && data.id === daily.id;

        if (isToday) {
            data.puzzle = daily.puzzle;
            data.images = daily.images;
        } else {
            const gameData: GameData = {
                id: data.id,
                size: config.b,
                turns: 0,
                images: data.images,
                puzzle: data.puzzle
            };

           localStorage.setItem('daily', btoa(JSON.stringify(gameData)));
        }

        loadPuzzle(data, isToday ? daily.size : config.b);

        if (isToday) {
            const results = JSON.parse(localStorage.getItem('results') || '[]');

            if (results.find((result: Record<string, number>) => result.p === data.id && result.s !== -1)) {
                setGameState('finished');
            } else if (daily.turns >= dailyMoves) {
                setGameState('over');
            }

            setTurns(daily.turns);
        }

        setDailyId(data.id);
    }, [config, dailyMoves, decodePuzzle, loadPuzzle]);

    const resetPuzzle = useCallback(() => {
        if (puzzle === null) {
            return;
        }

        const board: Board = [...chunk<number>(puzzle.puzzle, config.b)];

        setBoard(board);
        setGameState('active');
        setTurns(0);
    }, [config.b, puzzle]);

    useEffect(() => {
        if (! initialLoad.current) {
            loadDailyPuzzle();

            initialLoad.current = true;
        }
    }, [loadDailyPuzzle]);

    useEffect(() => {
        if (page !== 'daily') {
            if (dailyRefresh.current) {
                window.clearTimeout(dailyRefresh.current);
            }
        } else {
            const midnight = new Date();

            midnight.setHours(24, 0, 0);
            midnight.setMilliseconds(0);

            dailyRefresh.current = window.setTimeout(() => loadDailyPuzzle(), midnight.getTime() - new Date().getTime());
        }

        return () => {
            if (dailyRefresh.current) {
                window.clearTimeout(dailyRefresh.current);
            }
        }
    }, [page, loadDailyPuzzle]);

    const isPuzzleSolved = useCallback((board: number[], size: number): boolean => {
        const check = [...board];

        return check.every((value: number, index: number, board: number[]): boolean => {
            return !(board[index + 1] && board[index] > board[index + 1]);
        });
    }, []);

    const getCellState = useCallback((row: number, cell: number): string => {
        const correctValue = row * config.b + cell + 1;
        
        if (board[row][cell] === correctValue) {
            if (config.m === 'select' && firstSelection.current) {
                const boardElements = [...document.querySelectorAll('#board .cell-outer')];
                const firstIndex = boardElements.indexOf(firstSelection.current);
                const sourceRow = Math.floor(firstIndex / config.b);
                const sourceCell = firstIndex % config.b;

                if (sourceRow === row && sourceCell === cell) {
                    return 'cell-outer';
                }

                if (secondSelection.current) {
                    const secondIndex = boardElements.indexOf(secondSelection.current);
                    const targetRow = Math.floor(secondIndex / config.b);
                    const targetCell = secondIndex % config.b;

                    if (targetRow === row && targetCell === cell) {
                        return 'cell-outer';
                    }
                }
            }

            return 'cell-outer correct';
        }

        return 'cell-outer';
    }, [board, config]);

    const swapSelectedCells = () => {
        if (! firstSelection.current || ! secondSelection.current) {
            return;
        }

        const boardElements = [...document.querySelectorAll('#board .cell-outer')];
        const firstIndex = boardElements.indexOf(firstSelection.current);
        const row = Math.floor(firstIndex / config.b);
        const cell = firstIndex % config.b;
        const secondIndex = boardElements.indexOf(secondSelection.current);
        const targetRow = Math.floor(secondIndex / config.b);
        const targetCell = secondIndex % config.b;
        const updatedBoard = [...board];

        [updatedBoard[row][cell], updatedBoard[targetRow][targetCell]] = [board[targetRow][targetCell], board[row][cell]];

        setBoard(updatedBoard);
    };

    const swapCells = (row: number, cell: number, targetRow: number, targetCell: number): void => {
        if (! puzzleId) {
            return;
        }

        const updatedBoard = [...board];
        const currentMoves = turns + 1;

        if (config.m === 'drag') {
            [updatedBoard[row][cell], updatedBoard[targetRow][targetCell]] = [board[targetRow][targetCell], board[row][cell]];
        }

        setBoard(updatedBoard);
        setTurns(currentMoves);

        if (isPuzzleSolved(board.flat(), config.b)) {
            const results = JSON.parse(localStorage.getItem('results') || '[]');
            const summary = JSON.parse(localStorage.getItem('summary') || '{}');
            const score = stars[dailyMoves - currentMoves] ?? -1;
            const resultIndex = results.findIndex((result: PuzzleResult) => result.p === puzzleId);
            const result: PuzzleResult = {p: puzzleId, s: score};

            if (puzzleId === dailyId) {
                const stats = JSON.parse(localStorage.getItem('stats') || '[]');

                stats.push(result);
                localStorage.setItem('stats', JSON.stringify(stats));

                if (summary.p) {
                    if (summary.p === puzzleId - 1 && score >= 0) {
                        summary.c ++;
                        summary.b = summary.c > summary.b ? summary.c : summary.b;
                    } else {
                        summary.c = score >= 0 ? 1 : 0;
                    }

                    summary.g = summary.g + 1;
                    summary.p = puzzleId;
                    summary.s += Math.max(0, score);
                } else {
                    summary.g = 1;
                    summary.p = puzzleId;
                    summary.s = Math.max(0, score);
                    summary.c = summary.s >= 0 ? 1 : 0;
                    summary.b = summary.c;
                }

                localStorage.setItem('summary', JSON.stringify(summary));
            }

            fetch(`${process.env.REACT_APP_API_URL}/game`, {
                method: 'POST',
                body: JSON.stringify({'id': config.id, 'p': puzzleId, 's': score, 'b': config.b, 'm': config.m}),
                headers: new Headers({'content-type': 'application/json'}),
            });

            if (resultIndex === -1) {
                results.push(result);
            } else if (score > results[resultIndex].s) {
                results[resultIndex].s = score;
            }

            localStorage.setItem('results', JSON.stringify(results));

            setBoard(createOrderedBoard(config.b));
            setGameState('finished');
        } else if (currentMoves >= dailyMoves) {
            const results = JSON.parse(localStorage.getItem('results') || '[]');
            const summary = JSON.parse(localStorage.getItem('summary') || '{}');
            const resultIndex = results.findIndex((result: PuzzleResult) => result.p === puzzleId);
            const result: PuzzleResult = {p: puzzleId, s: -1};

            if (puzzleId === dailyId) {
                const stats = JSON.parse(localStorage.getItem('stats') || '[]');

                stats.push(result);
                localStorage.setItem('stats', JSON.stringify(stats));

                if (summary.p) {
                    summary.g = summary.g + 1;
                    summary.p = puzzleId;
                    summary.c = 0;
                } else {
                    summary.g = 1;
                    summary.p = puzzleId;
                    summary.s = 0;
                    summary.c = 0;
                    summary.b = 0;
                }

                localStorage.setItem('summary', JSON.stringify(summary));
            }

            fetch(`${process.env.REACT_APP_API_URL}/game`, {
                method: 'POST',
                body: JSON.stringify({'id': config.id, 'p': puzzleId, 's': -1}),
                headers: new Headers({'content-type': 'application/json'}),
            });

            if (resultIndex === -1) {
                results.push(result);
            }

            localStorage.setItem('results', JSON.stringify(results));

            setGameState('over');
        }

        if (puzzleId === dailyId) {
            const gameData: GameData = {
                id: puzzleId,
                size: config.b,
                turns: currentMoves,
                puzzle: board.flat(),
                images: images ?? []
            };

            localStorage.setItem('daily', btoa(JSON.stringify(gameData)));
        }
    };

    const getCellStyle = (value: number, face: 'front' | 'back'): Record<string, Record<string, string>> => {
        if (! puzzleId === null || images === null) {
            return {};
        }

        const style: Record<string, string> = {};

        if (value !== 0 && face === 'front') {
            style.backgroundImage = `url(${process.env.REACT_APP_IMG_URL}/${puzzleId}/${images.length}/${images[value - 1]}.png)`;
        }

        return {
            style
        }
    };

    const handleMouseDown = ({ nativeEvent: event }: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>, row: number, cell: number): void => {
        if (gameState !== 'active') {
            return;
        }

        if (config.m === 'select' || ! (event.target instanceof HTMLDivElement) || event.target.classList.contains('correct')) {
            return;
        }

        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

        drag.current = event.target;
        drag.current.classList.add('moving');
        drag.current.style.setProperty('z-index', String(++drags.current));

        dragData.current = {
            row,
            cell,
            position: {x: clientX, y: clientY}
        };
    }

    const handleMouseMove = ({ nativeEvent: event }: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>): void => {
        if (config.m === 'select' || ! (event.target instanceof HTMLDivElement) || ! event.target.classList.contains('cell-outer')) {
            return;
        }

        if (! drag.current || dragData.current === null) {
            return;
        }

        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

        drag.current.style.setProperty('left', `${clientX - dragData.current.position.x}px`);
        drag.current.style.setProperty('top', `${clientY - dragData.current.position.y}px`);
    }

    const handleMouseUp = ({ nativeEvent: event }: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>): void => {
        if (showMenu && event.target instanceof HTMLElement && ! event.target.closest('#menu')) {
            setShowMenu(false);

            return;
        }

        if (page !== 'daily') {
            return;
        }

        if (config.m === 'select') {
            if (! (event.target instanceof HTMLDivElement)) {
                return;
            }

            if (event.target.classList.contains('cell-outer')) {
                if (firstSelection.current && secondSelection.current) {
                    if (firstSelection.current !== event.target && secondSelection.current !== event.target) {
                        swapSelectedCells();
                    } else {
                        swapCells(0, 0, 0, 0);
                    }

                    firstSelection.current.classList.remove('selected');
                    secondSelection.current.classList.remove('selected');

                    firstSelection.current = null;
                    secondSelection.current = null;
                } else if (event.target.classList.contains('correct')) {
                    if (firstSelection.current) {
                        firstSelection.current.classList.remove('selected');
                        firstSelection.current = null;
                    }
                } else if (! firstSelection.current) {
                    firstSelection.current = event.target;
                    firstSelection.current.classList.add('selected');
                } else if (firstSelection.current && firstSelection.current === event.target) {
                    firstSelection.current.classList.remove('selected');
                    firstSelection.current = null;
                } else if (firstSelection.current) {
                    secondSelection.current = event.target;
                    secondSelection.current.classList.add('selected');

                    swapSelectedCells();
                }

                event.preventDefault();
            }

            return;
        }

        if (! drag.current || dragData.current === null) {
            return;
        }

        event.preventDefault();

        const clientX = event instanceof MouseEvent ? event.clientX : event.changedTouches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.changedTouches[0].clientY;
        const boardElements = [...document.querySelectorAll('#board .cell-outer')];
        const target = boardElements.find((element: Element) => {
            if (element === drag.current || element.classList.contains('correct')) {
                return false;
            }

            const bcr = element.getBoundingClientRect();

            if (clientX >= bcr.x && clientX <= bcr.x + bcr.width && clientY >= bcr.y && clientY <= bcr.y + bcr.height) {
                return true;
            }

            return false;
        });

        if (target instanceof HTMLDivElement && target.classList.contains('cell-outer')) {
            const index = boardElements.indexOf(target);
            const row = Math.floor(index / config.b);
            const cell = index % config.b;

            swapCells(dragData.current.row, dragData.current.cell, row, cell);
        }

        drag.current.classList.remove('moving');
        drag.current.style.setProperty('left', '0px');
        drag.current.style.setProperty('top', '0px');
        drag.current = null;
        dragData.current = null;
    }

    const shareResult = async (id: number, stars: number): Promise<any> => {
        const type = 'text/plain';
        const score = stars > 0 ? '⭐'.repeat(stars) : '0 ⭐';
        const text = [`Mosaic #${Intl.NumberFormat().format(id)}`, `${config.b} x ${config.b}`, score];
        const blob = new Blob([text.join("\n")], { type });
        const data = [new ClipboardItem({ [type]: blob })];

        await navigator.clipboard.write(data);

        setCopied(true);
    };

    let displayedBoardSize = config.b;

    if (puzzleId === dailyId) {
        const daily = JSON.parse(atob(localStorage.getItem('daily') || 'e30='));

        displayedBoardSize = daily.size ?? config.b;
    }

    const remainingMoves = moves[displayedBoardSize] - turns;

    return (
        <div 
            className="App" 
            onMouseMove={(event: React.MouseEvent<HTMLElement>) => handleMouseMove(event)}
            onMouseUp={(event: React.MouseEvent<HTMLElement>) => handleMouseUp(event)} 
            onTouchMove={(event: React.TouchEvent<HTMLElement>) => handleMouseMove(event)}
            onTouchEnd={(event: React.TouchEvent<HTMLElement>) => handleMouseUp(event)}>
            <Header 
                isDark={isDark}
                setScheme={setScheme}
                setShowMenu={setShowMenu} 
                setShowSettings={setShowSettings}
                setShowStatistics={setShowStatistics}
                showMenu={showMenu} 
                showSettings={showSettings}
                showStatistics={showStatistics}
            />
            <Menu 
                showMenu={showMenu} 
                loadDailyPuzzle={loadDailyPuzzle} 
                setPage={setPage} 
                setShowMenu={setShowMenu} 
                setShowSettings={setShowSettings}
                setShowStatistics={setShowStatistics} 
            />
            <Settings config={config} modal={true} setShowSettings={setShowSettings} visible={showSettings} />
            <Statistics config={config} dailyId={dailyId} setShowStatistics={setShowStatistics} visible={showStatistics} />
            {page === 'archive' && <Archive boardSize={config.b} decodePuzzle={decodePuzzle} loadPuzzle={loadPuzzle} />}
            {page === 'classic' && <Classic dimensions={dimensions} />}
            {page === 'daily' && <div id="game">
                {gameState === 'loading' && <Loading />}
                {gameState !== 'loading' && <>
                    <div id="title">Daily Mosaic #{puzzleId}</div>
                    <div id="game-outer">
                        <div id="board-background" className={`grid-${displayedBoardSize} daily ${gameState}`}>
                            {board.map((row, rowKey) => (
                                <Fragment key={rowKey}>
                                {row.map((cell, cellKey) => (
                                    <div key={cellKey} className="cell-outer-back" style={{width: cellSize - 2, height: cellSize - 2}}></div>
                                ))}
                                </Fragment>
                            ))}
                        </div>
                        <div id="board" className={`grid-${displayedBoardSize} daily ${gameState}`} style={{'--cell-size': `${cellSize}px`} as React.CSSProperties}>
                            {board.map((row, rowKey) => (
                                <Fragment key={rowKey}>
                                {row.map((cell, cellKey) => (
                                    <div 
                                        key={cellKey} 
                                        className={getCellState(rowKey, cellKey)}
                                        onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => handleMouseDown(event, rowKey, cellKey)}
                                        onTouchStart={(event: React.TouchEvent<HTMLDivElement>) => handleMouseDown(event, rowKey, cellKey)}>
                                        <div className="cell-front" {...getCellStyle(cell, 'front')} />
                                        <div className="cell-back"{...getCellStyle(cell, 'back')} />
                                    </div>
                                ))}
                                </Fragment>
                            ))}
                        </div>
                    </div>
                    <div id="moves">
                        <span className="count">{Math.max(0, remainingMoves)}</span> {remainingMoves === 1 ? 'move' : 'moves'} remaining
                        {gameState === 'finished' && <div className="result">
                            {<div id="score-name">{scores[remainingMoves]}</div>}
                            {remainingMoves > 0 && <div id="score-stars">
                                {[...Array(stars[remainingMoves]).keys()].map(k => <Star key={k} className="active" />)}
                            </div>}
                            {remainingMoves === 0 && <div id="score-stars">No stars this time</div>}
                            {puzzleId && dailyId && puzzleId === dailyId && <button className="button copy" onClick={() => shareResult(dailyId, stars[remainingMoves])}>
                                {copied && 'Result Copied'}
                                {!copied && 'Share'}
                            </button>}
                        </div>}
                        {gameState === 'over' && <div className="result">
                            {<div id="score-name">Game Over</div>}
                            {puzzleId && dailyId && puzzleId !== dailyId && <button className="button play" onClick={() => resetPuzzle()}>Try Again</button>}
                            {puzzleId && dailyId && puzzleId === dailyId && <div id="score-stars">No stars this time</div>}
                        </div>}
                    </div>
                </>}
            </div>}
        </div>
    );
}

export default Game;
